<template>
  <div class="sign-in px-2">
    <img
      :src="`${apiURL}/logo/group-logo.svg`"
      width="155px"
      height="92px"
      class="sign-in__logo"
    >
    <b-form
      ref="signInForm"
      class="sign-in__form"
      @submit.prevent="signIn"
    >
      <div class="sign-in__header mb-4">
        <h2 class="sign-in__header-text mb-0">
          {{ $t('Sign in') }}
        </h2>
        <dx-select-box
          :items="languageTypes"
          styling-mode="outlined"
          field-template="field"
          item-template="item"
          class="choose-language"
          display-expr="item"
          :value="displayLanguageTypes"
        >
          <template #item="{ data }">
            <div
              class="d-flex flex-row align-items-center"
              @click.prevent="changeLocale(data.language)"
            >
              <img
                :src="require('/src/icons/'+ data.icon)"
                class="mr-1"
              >
              <p class="mb-0">
                {{ data.text }}
              </p>
            </div>
          </template>
          <template #field="{ data }">
            <div class="d-flex flex-row align-items-center">
              <img
                v-if="data"
                :src="require('/src/icons/'+ data.icon)"
                class="mr-1"
              >
              <dx-text-box
                :value=" data.icon && data.text"
                :read-only="true"
              />
            </div>
          </template>
        </dx-select-box>
      </div>

      <label class="label">
        {{ $t('User name') }}
      </label>
      <div class="mb-3 input-group">
        <input
          v-model.trim="username"
          type="text"
          class="form-control"
          :class="{'invalid': errors && errors.username}"
        >
        <template v-if="errors && errors.username">
          <div
            v-for="(error, i) in errors.username"
            :key="i"
            class="my-1 invalid-text"
          >
            {{ error }}
          </div>
        </template>
      </div>

      <label class="label">
        {{ $t('Password') }}
      </label>
      <div class="mb-3 input-group">
        <input
          v-if="showPassword"
          v-model.trim="password"
          type="text"
          class="form-control"
          :class="{'invalid': errors && errors.password}"
        >
        <input
          v-else
          v-model.trim="password"
          type="password"
          class="form-control"
          :class="{'invalid': errors && errors.password }"
        >
        <div class="input-group-btn">
          <button
            type="button"
            class="input-group-btn password__button"
            @click.prevent="toggleShow"
          >
            <i
              class="sign-in__eye"
              :class="{ 'eye-open': showPassword, 'eye-closed': !showPassword }"
            ></i>
          </button>
        </div>
        <template v-if="errors && errors.password">
          <div
            v-for="(error, i) in errors.password"
            :key="i"
            class="my-1 invalid-text"
          >
            {{ error }}
          </div>
        </template>
      </div>

      <dx-button
        :text="$t('Sign in')"
        type="success"
        class="custom-btn w-100 mt-2"
        :disabled="loading"
        :use-submit-behavior="true"
      />
      <div class="my-1">
        <p
          v-if="errors && errors.detail"
          class="my-0 invalid-text text-center"
        >
          {{ errors.detail }}
        </p>
      </div>
    </b-form>
  </div>
</template>

<script>
import { DxButton, DxTextBox } from 'devextreme-vue';
import DxSelectBox from 'devextreme-vue/select-box';

export default {
  name: 'SignIn',
  components: {
    DxButton,
    DxTextBox,
    DxSelectBox,
  },
  data: () => ({
    loading: false,
    errors: null,
    username: '',
    password: '',
    showPassword: false,
    apiURL: process.env.VUE_APP_API_URL,
    languageTypes: [
      {
        language: 'ru',
        text: 'Рус',
        icon: 'icon-russian.svg',
      },
      {
        language: 'en',
        text: 'Eng',
        icon: 'icon-english.svg',
      },
      {
        language: 'cn',
        text: 'Chi',
        icon: 'icon-chinese.svg',
      },
    ],
  }),
  computed: {
    displayLanguageTypes() {
      return this.languageTypes.filter(it => it.language === this.$i18n.locale)[0];
    },
  },

  methods: {
    toggleShow() {
      this.showPassword = !this.showPassword;
    },
    changePasswordMode() {
      this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
    },
    async signIn() {
      this.loading = true;
      this.errors = null;

      try {
        const { access, refresh } = await this.$http.post('api/token/', {
          username: this.username,
          password: this.password,
        });

        this.$http.defaults.headers.authorization = `JWT ${access}`;
        localStorage.setItem('access-token', access);
        localStorage.setItem('refresh-token', refresh);

        const user = await this.$http.get('/profile/api/users/me/');
        this.$store.commit('logIn', user);

        Object.assign(this.$data, this.$options.data());
      } catch (e) {
        this.errors = e.response.data;
      } finally {
        this.loading = false;
      }
    },
    changeLocale(lang) {
      localStorage.setItem('locale', lang);
      this.$i18n.locale = lang;
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-in {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../assets/sign-in-background.jpg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  &__header-text {
    font-size: 24px;
    @media(max-width: 992px) {
      font-size: 20px;
    }
  }

  &__logo {
    margin: 0 auto 10px auto;
  }

  &__form {
    height: fit-content;
    margin: auto auto;
    padding: 40px;
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 6px 1px rgba(0, 0, 0, 0.3);
    box-sizing: content-box;
    @media(max-width: 1920px) {
      width: 420px;
    }
    @media(max-width: 1024px) {
      width: 380px;
    }
    @media(max-width: 800px) {
      width: 300px;
    }
    @media(max-width: 533px) {
      width: fit-content;
    }
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  &__lang-btn-group {
    .dx-button {
      box-shadow: none;
      border: 1px solid var(--main-blue);
      border-radius: 2px;

      &.dx-button-default.dx-state-focused {
        background-color: var(--main-blue);
        box-shadow: none;
      }
    }
  }

  .label {
    font-size: 12px;
  }

  .input-group {
    align-items: center;
    .form-control {
      position: relative;
      width: 100%;
      height: 48px;
      padding: 15px 16px 14px;
      box-sizing: border-box;
      border: none;
      border-radius: 8px !important;
      box-shadow: inset 0 0 0 1px #e0e6ee;
      font-weight: 600;
      color: black;
      transition: .4s cubic-bezier(.23, 1, .32, 1);
      &:hover {
        box-shadow: inset 0 0 0 1px #8e9eb6;
      }
      &:focus {
        box-shadow: inset 0 0 0 2px #3c5ac3;
      }
    }
  }

  .password__button {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 35px;
    height: 30px;
    border: none;
    background-color: transparent;
    z-index: 10;
    .password__button:focus {
      border: none;
    }
  }

  &__eye {
    display: block;
    width: 24px;
    height: 24px;
  }

  .eye-open {
    background-image: url('../icons/icon-eyeopen.svg');
    background-repeat: no-repeat;
    background-position: 50% 50%;
  }

  .eye-closed {
    background-image: url('../icons/icon-eyeclose.svg');
    background-repeat: no-repeat;
    background-position: 50% 50%;
  }

  .invalid-text {
    color: #E83434 !important;
    font-size: 14px;
  }

  .invalid {
    box-shadow: inset 0 0 0 1px #E83434 !important;
    &:focus {
      box-shadow: inset 0 0 0 2px #E83434 !important;
    }
  }

  .choose-language {
    width: 6rem;
    height: fit-content;
    padding-left: 0.2rem;
    margin-bottom: 0;
    box-shadow: inset 0 0 0 2px transparent !important;
    &.dx-dropdowneditor-active {
      box-shadow: inset 0 0 0 2px #3C5AC3 !important;
      @media(max-width: 992px) {
        box-shadow: 0 0 0 2px transparent !important;
      }
    }
  }
  ::v-deep input.dx-texteditor-input {
    color: black !important;
    width: 30px !important;
    padding: 8px 0 !important;
  }
}
</style>
